import React, { useRef, useState, useEffect } from "react"
import { BiXCircle } from "react-icons/bi"
import {
  IoMdArrowDropdown,
  IoMdArrowDropleft,
  IoMdSearch,
} from "react-icons/io"
import { MdArrowDropDown } from "react-icons/md"
import { useSelector } from "react-redux"
import { useOnClickOutSide } from "tools/hooks/click-outSide-handler"

interface DropdownTreeProps {
  selectedNodes?: { id: string; label: string }[]
  setSelectedNodes?: React.Dispatch<
    React.SetStateAction<{ id: string; label: string }[]>
  >
  type: "select" | "text"
  wrapperClassName?: string
  initialValue?: string
  singleSelect?: boolean
  onSelect?: (selectedNodes: { id: string; label: string }[]) => void
  showChildren?: boolean
  customTree?: any
  error?: string
  important?: boolean
}

type TreeNode = {
  id: string
  label: string
  children: TreeNode[]
  isActive?: boolean
}

const InputTreeSelect: React.FC<DropdownTreeProps> = ({
  selectedNodes,
  setSelectedNodes,
  type,
  wrapperClassName,
  initialValue,
  singleSelect = false,
  onSelect,
  showChildren = true,
  customTree,
  error,
  important,
}: DropdownTreeProps) => {
  const WrapperRef = useRef(null)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())
  const userInfo = useSelector((state: any) => state.userInfo)
  const treeData = customTree || userInfo.hublist
  const [searchTerm, setSearchTerm] = useState("")
  const [isOpen, setIsOpen] = useState(type === "select" ? false : true)

  useOnClickOutSide(WrapperRef, isOpen, setIsOpen)

  useEffect(() => {
    const initialExpanded = new Set(treeData?.map((node: TreeNode) => node.id))
    setExpandedNodes(initialExpanded)
  }, [treeData])
  const toggleParent = (id: string, label: string, children?: TreeNode[]) => {
    let updatedSelection = [...selectedNodes]
    const isParentSelected = updatedSelection.some((node) => node.id === id)

    const processChildren = (nodes: TreeNode[], shouldAdd: boolean) => {
      nodes.forEach((child) => {
        const isChildSelected = updatedSelection.some(
          (node) => node.id === child.id
        )
        if (shouldAdd && !isChildSelected) {
          updatedSelection.push({ id: child.id, label: child.label })
        } else if (!shouldAdd && isChildSelected) {
          const index = updatedSelection.findIndex(
            (node) => node.id === child.id
          )
          if (index !== -1) {
            updatedSelection.splice(index, 1)
          }
        }
        if (child.children && child.children.length > 0) {
          processChildren(child.children, shouldAdd)
        }
      })
    }

    if (singleSelect) {
      updatedSelection = [{ id, label }]
    } else if (id === 1) {
      if (isParentSelected) {
        const index = updatedSelection.findIndex((node) => node.id === id)
        if (index !== -1) updatedSelection.splice(index, 1)
        if (children) processChildren(children, false)
      } else {
        updatedSelection.push({ id, label })
        if (children) processChildren(children, true)
      }
    } else if (isParentSelected) {
      const index = updatedSelection.findIndex((node) => node.id === id)
      if (index !== -1) updatedSelection.splice(index, 1)
    } else {
      updatedSelection.push({ id, label })
      if (children) processChildren(children, true)
    }

    setSelectedNodes(updatedSelection)
    onSelect(updatedSelection)
  }

  const toggleChild = (id: string, label: string) => {
    let updatedSelection = [...selectedNodes]
    const isChildSelected = updatedSelection.some((node) => node.id === id)

    if (singleSelect) {
      updatedSelection = [{ id, label }]
    } else {
      if (isChildSelected) {
        updatedSelection = updatedSelection.filter((node) => node.id !== id)
      } else {
        updatedSelection.push({ id, label })
      }
    }

    setSelectedNodes(updatedSelection)
    onSelect(updatedSelection)
  }
  const toggleExpand = (id: string) => {
    const updatedExpanded = new Set(expandedNodes)
    if (updatedExpanded.has(id)) {
      updatedExpanded.delete(id)
    } else {
      updatedExpanded.add(id)
    }
    setExpandedNodes(updatedExpanded)
  }

  const filterTree = (nodes: TreeNode[], searchTerm: string): TreeNode[] => {
    return nodes
      .map((node) => {
        const hasChildren = node.children?.length
        const labelMatches = node.label.includes(searchTerm)
        const filteredChildren = hasChildren
          ? filterTree(node.children!, searchTerm)
          : []
        if (labelMatches || filteredChildren.length > 0) {
          return { ...node, children: filteredChildren }
        }
        return null
      })
      .filter(Boolean) as TreeNode[]
  }

  const filteredTreeData =
    searchTerm.trim() === "" ? treeData : filterTree(treeData, searchTerm)

  const renderTree = (nodes: TreeNode[]) => {
    const showparents = !showChildren
      ? nodes?.filter((node) => node.parent === 1 || node.parent === null)
      : nodes
    return showparents.map((node) => {
      const isChecked = selectedNodes.some((n) => n.id === node.id)
      const isExpanded = expandedNodes.has(node.id)
      const hasChildren = node.children.length !== 0
      const isDisabled = type === "select" ? false : !node.isActive

      return (
        <div
          key={node.id}
          style={{ marginRight: showChildren ? (hasChildren ? 16 : 30) : 16 }}
          className=""
        >
          {hasChildren && showChildren && (
            <span
              className="cursor-pointer mr-1 text-tomato font-semibold inline-block"
              onClick={(e) => {
                e.stopPropagation()
                toggleExpand(node.id)
              }}
            >
              {isExpanded ? <IoMdArrowDropdown /> : <IoMdArrowDropleft />}
            </span>
          )}
          <input
            disabled={isDisabled}
            type="checkbox"
            checked={isChecked}
            onChange={() =>
              hasChildren
                ? toggleParent(node.id, node.label, node.children)
                : toggleChild(node.id, node.label)
            }
            style={{ accentColor: "tomato" }}
          />
          <label
            className="cu cursor-pointer  mr-1"
            style={{ userSelect: "none" }}
          >
            {node.label}
          </label>
          {hasChildren && isExpanded && (
            <div style={{ marginLeft: 20 }}>{renderTree(node.children!)}</div>
          )}
        </div>
      )
    })
  }

  const deleteNode = (id: string, children?: TreeNode[]) => {
    setSelectedNodes((prevSelectedNodes) => {
      const childIds = children?.map((child) => child.id) || []
      return prevSelectedNodes.filter(
        (node) => node.id !== id && !childIds.includes(node.id)
      )
    })
  }

  return (
    <div className={`relative ${wrapperClassName}`} ref={WrapperRef}>
      <label
        className={` absolute top-[-16px] right-5 bg-white z-10  px-2  text-sm `}
      >
        {"هاب"}
        <span className="text-tomato mr-2">{important ? "*" : " "}</span>
      </label>

      <span className="text-tomato absolute top-[-10px] left-5 bg-white z-10  px-2  text-sm"></span>
      <div className="flex flex-wrap rounded-lg border p-[6px] border-gray-400">
        <div className="flex justify-between w-full items-center">
          <input
            type="text"
            value={type === "select" ? initialValue : searchTerm}
            onChange={(e) => {
              setIsOpen(true)
              setSearchTerm(e.target.value)
            }}
            className="autocompleteInput mt-0"
            style={{
              width: "100%",
              boxSizing: "border-box",
            }}
          />
          {type === "select" ? (
            <MdArrowDropDown size={25} onClick={() => setIsOpen(!isOpen)} />
          ) : (
            <IoMdSearch size={25} />
          )}
        </div>

        {type !== "select" && (
          <div className="max-h-28 overflow-y-auto w-full">
            {selectedNodes.map((node) => (
              <div
                key={node.id}
                className="bg-[#EDF3FF] w-fit p-1  flex-between-center  rounded-xl shadow whitespace-nowrap inline-flex  m-1"
              >
                <span className="text-darkGray">{node.label}</span>
                <span
                  className="cursor-pointer mr-2"
                  onClick={() => {
                    const parentNode = treeData.find(
                      (n: any) => n.id === node.id
                    )
                    deleteNode(node.id, parentNode?.children)
                  }}
                >
                  <BiXCircle size={18} className="text-dark" />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      {isOpen && (
        <div
          className=" absolute  w-full bg-white z-[9999] shadow-md "
          style={{ marginTop: "10px", maxHeight: "300px", overflowY: "auto" }}
        >
          {type === "select" && (
            <div className="flex justify-between w-full items-center my-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setIsOpen(true)
                  setSearchTerm(e.target.value)
                }}
                className="autocompleteInput  py-2"
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                }}
                placeholder="جستجو ..."
              />
              <IoMdSearch size={25} />
            </div>
          )}

          {renderTree(filteredTreeData)}
        </div>
      )}
      <p className="text-red text-xs pr-3 h-4 mt-1">{error}</p>
    </div>
  )
}

export default InputTreeSelect
